/** Kornit printer settings — dedicated sidebar (opened from Integrations). */
export const KORNIT_SETTINGS_PREFIX = "/settings/kornit";

export function isKornitSettingsPath(pathname) {
  return /^\/settings\/kornit(?:\/|$)/.test(pathname || "");
}

export const RIVERR_LAST_NON_KORNIT_PATH_KEY = "riverr_last_non_kornit_path";

export function kornitPathFor(segment) {
  return `${KORNIT_SETTINGS_PREFIX}/${segment}`;
}

/**
 * IA matching riverr-kornit-settings.html: Overview, then "Build the map"
 * (numbered), then "Use it".
 */
export const KORNIT_NAV_GROUPS = [
  {
    id: "top",
    label: null,
    items: [
      { id: "overview", label: "Overview", to: kornitPathFor("overview") },
    ],
  },
  {
    id: "build",
    label: "Build the map",
    items: [
      { id: "machines", label: "1 · Machines", to: kornitPathFor("machines") },
      { id: "setups", label: "2 · Setups (KST)", to: kornitPathFor("setups") },
      {
        id: "defaults",
        label: "3 · Defaults per machine",
        to: kornitPathFor("defaults"),
      },
      {
        id: "style-setups",
        label: "4 · Style setups",
        to: kornitPathFor("style-setups"),
      },
      { id: "colors", label: "Color classes", to: kornitPathFor("colors") },
      {
        id: "print-order",
        label: "Print order",
        to: kornitPathFor("print-order"),
      },
    ],
  },
  {
    id: "use",
    label: "Use it",
    items: [
      { id: "test-scan", label: "Test a scan", to: kornitPathFor("test-scan") },
      {
        id: "import-review",
        label: "Import review",
        to: kornitPathFor("import-review"),
      },
      { id: "advanced", label: "Advanced", to: kornitPathFor("advanced") },
    ],
  },
];

export const KORNIT_NAV = KORNIT_NAV_GROUPS.flatMap((g) => g.items);

function isPathActive(pathname, target) {
  if (!target) return false;
  return pathname === target || pathname.startsWith(`${target}/`);
}

export function isKornitNavItemActive(pathname, item) {
  return isPathActive(pathname, item?.to);
}

const checklistOf = (readiness, id) =>
  (readiness?.checklist || []).find((item) => item.id === id);

const leadingCount = (detail) => {
  const m = String(detail || "").match(/(\d+)/);
  return m ? m[1] : null;
};

const toneFromStatus = (status) =>
  status === "ready" ? "ok" : status === "bad" ? "bad" : "warn";

/**
 * Readiness badges for the left rail (mockup `.b` / `.b.ok`).
 */
export function kornitNavBadge(itemId, readiness) {
  if (!readiness) return null;

  if (itemId === "overview") {
    const issues = (readiness.checklist || []).filter(
      (i) => i.status && i.status !== "ready"
    ).length;
    return issues
      ? { text: `${issues} issue${issues === 1 ? "" : "s"}`, tone: "bad" }
      : { text: "ready", tone: "ok" };
  }

  if (itemId === "machines") {
    const item = checklistOf(readiness, "machines");
    return {
      text: leadingCount(item?.detail) || "0",
      tone: toneFromStatus(item?.status),
    };
  }

  if (itemId === "setups") {
    const item = checklistOf(readiness, "setups");
    return {
      text: leadingCount(item?.detail) || "0",
      tone: toneFromStatus(item?.status),
    };
  }

  if (itemId === "defaults") {
    const item = checklistOf(readiness, "defaults");
    const n = leadingCount(item?.detail);
    return {
      text: n ? `${n} cells` : "—",
      tone: toneFromStatus(item?.status),
    };
  }

  if (itemId === "style-setups") {
    const item = checklistOf(readiness, "styleSetups");
    const proposed = /proposed/i.test(item?.detail || "")
      ? leadingCount(item?.detail)
      : null;
    if (proposed) {
      return { text: `${proposed} to review`, tone: "bad" };
    }
    return { text: "ok", tone: "ok" };
  }

  if (itemId === "colors") {
    return { text: "auto", tone: "ok" };
  }

  return null;
}
